// src/components/TabContent.tsx
import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Feed from './Feed.tsx';
import { Profile } from './Profile.tsx';
import { TrainerDetail } from './TrainerDetail.tsx';
import { PostModal } from './PostModal.tsx';
import CreatePostModal from './CreatePostModal.tsx';
import { trainersAPI, Trainer } from '../services/api.ts';
import { useAuth } from '../contexts/AuthContext.tsx';
import { BookingDashboard } from './BookingDashboard.tsx';
import { AdminPanel } from './AdminPanel.tsx';
import SearchComponent from './Searchbar.tsx';

interface TabContentProps {
  activeTab: string;
  onTabChange: (tab: string) => void;
  onViewUser: (userId: string) => void;
}

interface PostResponse {
  success: boolean;
  post_id: string;
  media_url?: string;
  thumbnail_url?: string;
  message: string;
  error?: string;
}

interface ServiceInfo {
  name: string;
  desc: string;
  fullDescription: string;
  features: string[];
  benefits: string[];
}

// Service Modal Component
function ServiceModal({ service, onClose, onFindTrainers }: { service: ServiceInfo; onClose: () => void; onFindTrainers: () => void }) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Modal Header */}
        <div className="sticky top-0 bg-white border-b p-6 flex justify-between items-center">
          <h2 className="text-2xl font-bold text-gray-800">{service.name}</h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
          >
            ×
          </button>
        </div>

        {/* Modal Content */}
        <div className="p-6">
          {/* Description */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">About This Service</h3>
            <p className="text-gray-600 leading-relaxed">{service.fullDescription}</p>
          </div>

          {/* Features */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">What's Included</h3>
            <ul className="space-y-2">
              {service.features.map((feature, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-[#9CAF88] mr-2 mt-1">✓</span>
                  <span className="text-gray-600">{feature}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Benefits */}
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-3">Key Benefits</h3>
            <ul className="space-y-2">
              {service.benefits.map((benefit, index) => (
                <li key={index} className="flex items-start">
                  <span className="text-[#E6A756] mr-2 mt-1">★</span>
                  <span className="text-gray-600">{benefit}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Action Button */}
          <div className="flex justify-center">
            <button
              onClick={onFindTrainers}
              className="bg-gradient-to-r from-[#E6A756] to-[#D2824C] text-[#5D4037] px-8 py-3 rounded-full font-semibold hover:from-[#D2824C] hover:to-[#A0673D] transition-all"
            >
              Find Trainers
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function TabContent({ activeTab, onTabChange, onViewUser }: TabContentProps) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { trainerId } = useParams();
  const [trainers, setTrainers] = useState<Trainer[]>([]);
  const [loadingTrainers, setLoadingTrainers] = useState(false);
  const [selectedTrainer, setSelectedTrainer] = useState<string | null>(null);
  const [selectedPost, setSelectedPost] = useState<string | null>(null);
  const [isCreatePostModalOpen, setIsCreatePostModalOpen] = useState(false);

  // Add state for service modal
  const [selectedService, setSelectedService] = useState<ServiceInfo | null>(null);

  // Define detailed service information
  const services: ServiceInfo[] = [
    {
      name: 'Personal Training',
      desc: 'One-on-one sessions tailored to your goals',
      fullDescription: 'Our personal training sessions provide you with dedicated, one-on-one attention from certified fitness professionals. Each session is customized to your specific goals, fitness level, and preferences, ensuring maximum results and safety.',
      features: [
        'Personalized workout plans',
        'Form correction and technique guidance',
        'Progress tracking and assessments',
        'Nutritional guidance',
        'Flexible scheduling',
        'Equipment orientation'
      ],
      benefits: [
        'Faster results with targeted exercises',
        'Reduced risk of injury',
        'Improved motivation and accountability',
        'Expert guidance and support',
        'Customized approach to your needs'
      ]
    },
    {
      name: 'Group Classes',
      desc: 'Fun, motivating workouts with others',
      fullDescription: 'Join our energetic group fitness classes designed to keep you motivated while working out alongside like-minded individuals. Our classes combine effective workouts with a supportive community atmosphere.',
      features: [
        'Variety of class types (HIIT, Yoga, Strength)',
        'All fitness levels welcome',
        'Professional instruction',
        'Community support',
        'Equipment provided',
        'Multiple time slots available'
      ],
      benefits: [
        'Increased motivation through group energy',
        'Cost-effective fitness solution',
        'Social connections and friendships',
        'Structured workout routines',
        'Fun and engaging atmosphere'
      ]
    },
    {
      name: 'Nutrition Coaching',
      desc: 'Expert guidance for optimal nutrition',
      fullDescription: 'Transform your relationship with food through our comprehensive nutrition coaching program. Our registered dietitians work with you to create sustainable eating habits that support your fitness goals and overall health.',
      features: [
        'Personalized meal planning',
        'Macro and calorie calculations',
        'Food diary analysis',
        'Supplement recommendations',
        'Recipe suggestions',
        'Regular check-ins and adjustments'
      ],
      benefits: [
        'Sustainable weight management',
        'Improved energy levels',
        'Better athletic performance',
        'Enhanced overall health',
        'Long-term lifestyle changes'
      ]
    },
    {
      name: 'Online Training',
      desc: 'Virtual sessions from anywhere',
      fullDescription: 'Access professional fitness coaching from the comfort of your home or while traveling. Our online training platform provides live and on demand sessions with real time feedback and support.',
      features: [
        'Live virtual training sessions',
        'On-demand workout library',
        'Mobile app access',
        'Progress tracking tools',
        'Chat support with trainers',
        'Equipment-free options available'
      ],
      benefits: [
        'Convenience and flexibility',
        'No travel time required',
        'Access to top trainers globally',
        'Consistent training schedule',
        'Cost effective option'
      ]
    },
    {
      name: 'Wellness Programs',
      desc: 'Comprehensive health and wellness plans',
      fullDescription: 'Our holistic wellness programs address all aspects of your health, combining fitness, nutrition, stress management, and lifestyle coaching to help you achieve optimal well being.',
      features: [
        'Comprehensive health assessments',
        'Stress management techniques',
        'Sleep optimization strategies',
        'Mindfulness and meditation',
        'Lifestyle coaching',
        'Monthly progress reviews'
      ],
      benefits: [
        'Improved overall quality of life',
        'Better stress management',
        'Enhanced mental clarity',
        'Increased energy and vitality',
        'Sustainable healthy habits'
      ]
    },
    {
      name: 'Recovery Sessions',
      desc: 'Specialized recovery and mobility work',
      fullDescription: 'Enhance your recovery and prevent injuries with our specialized sessions focusing on mobility, flexibility, and muscle recovery. Perfect for athletes and anyone looking to improve their movement quality.',
      features: [
        'Mobility assessments',
        'Stretching routines',
        'Foam rolling techniques',
        'Injury prevention strategies',
        'Recovery planning',
        'Movement pattern correction'
      ],
      benefits: [
        'Reduced muscle soreness',
        'Improved flexibility and mobility',
        'Faster recovery between workouts',
        'Injury prevention',
        'Better movement quality'
      ]
    }
  ];

  // Update selectedTrainer when URL changes
  useEffect(() => {
    if (trainerId && activeTab === 'trainers') {
      setSelectedTrainer(trainerId);
    } else if (!trainerId) {
      setSelectedTrainer(null);
    }
  }, [trainerId, activeTab]);

  // Fetch trainers when trainers tab is active
  useEffect(() => {
    if (activeTab === 'trainers') {
      fetchTrainers();
    }
  }, [activeTab]);

  const fetchTrainers = async () => {
    setLoadingTrainers(true);
    try {
      const response = await trainersAPI.getAll();
      setTrainers(response.data.trainers);
    } catch (error) {
      console.error('Failed to fetch trainers:', error);
    } finally {
      setLoadingTrainers(false);
    }
  };

  // Handle post creation success
  const handlePostCreated = (newPost: PostResponse) => {
    console.log('New post created:', newPost);
  };

  // Update trainer selection to use navigation
  const handleSelectTrainer = (trainerId: string) => {
    setSelectedTrainer(trainerId);
    navigate(`/trainers/${trainerId}`);
  };

  const handleCloseTrainer = () => {
    setSelectedTrainer(null);
    navigate('/trainers');
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return (
          <div className="text-center">
            <h1 className="text-5xl md:text-6xl mb-6 text-gray-800">
              Welcome to AZTrainers
            </h1>
            <p className="text-xl md:text-2xl text-gray-700 mb-8 max-w-2xl mx-auto">
              Connect with certified trainers, share your fitness journey, and build a supportive community
            </p>

            {/* Personalized welcome message */}
            {user && (
              <div className="bg-white/60 backdrop-blur-sm rounded-lg p-6 border border-white/40 shadow-lg mb-8 max-w-md mx-auto">
                <p className="text-lg text-gray-800">
                  Welcome back, <span className="font-semibold">{user.name}!</span>
                </p>
                <p className="text-sm text-gray-600 mt-2">
                  Ready to continue your fitness journey?
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <button
                onClick={() => navigate('/trainers')}
                className="bg-white/80 text-[#5D4037] px-8 py-3 rounded-full hover:bg-white transition-colors shadow-lg border border-[#D4C4B0]/30 backdrop-blur-sm"
              >
                Find a Trainer
              </button>
              <button
                onClick={() => navigate('/feed')}
                className="bg-[#E6A756] text-[#5D4037] px-8 py-3 rounded-full hover:bg-[#D2824C] transition-colors shadow-lg"
              >
                Join the Community
              </button>
            </div>
          </div>
        );

      case 'feed':
        return (
          <div className="w-full">
            {/* Feed Header with Create Post Button */}
            <div className="bg-white/60 backdrop-blur-sm rounded-lg p-6 border border-white/40 shadow-lg mb-6 max-w-2xl mx-auto">
              <div className="flex justify-between items-center">
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">Social Feed</h2>
                  <p className="text-gray-600">Share your fitness journey with the community</p>
                </div>
                <button
                  onClick={() => setIsCreatePostModalOpen(true)}
                  className="bg-gradient-to-r from-[#E6A756] to-[#D2824C] text-[#5D4037] px-6 py-3 rounded-full font-semibold hover:from-[#D2824C] hover:to-[#A0673D] transition-all transform hover:scale-105 shadow-lg"
                >
                  Create Post
                </button>
              </div>
            </div>

            {/* Feed Component */}
            <Feed onNavigateToProfile={onViewUser} />
          </div>
        );

      case 'bookings':
        return <BookingDashboard />;

      case 'profile':
        return <Profile onViewUser={onViewUser} />;

      case 'search':
        return (
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl mb-6 text-gray-800">
              Search
            </h1>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Find users, trainers, and posts across the AZTrainers community
            </p>
            <div className="max-w-4xl mx-auto">
              <SearchComponent
                onNavigateToProfile={onViewUser}
                onNavigateToTrainer={(trainerId) => {
                  navigate(`/trainers/${trainerId}`);
                }}
                onNavigateToPost={(postId) => {
                  setSelectedPost(postId);
                }}
              />
            </div>
          </div>
        );

      case 'trainers':
        return (
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl mb-6 text-gray-800">
              Our Expert Trainers
            </h1>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Meet our certified fitness professionals who are dedicated to helping you achieve your health and fitness goals
            </p>

            {loadingTrainers ? (
              <div className="flex justify-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-800"></div>
              </div>
            ) : (
              <div className="grid md:grid-cols-3 gap-8 max-w-4xl mx-auto">
                {trainers.length > 0 ? (
                  trainers.map((trainer) => (
                    <div
                      key={trainer.id}
                      className="bg-white/60 backdrop-blur-sm rounded-lg p-6 border border-white/40 shadow-lg hover:bg-white/80 transition-all group cursor-pointer"
                      onClick={() => handleSelectTrainer(trainer.id)}
                    >
                      <div className="w-24 h-24 bg-gray-200 rounded-full mx-auto mb-4 overflow-hidden">
                        {trainer.profile_picture ? (
                          <img
                            src={trainer.profile_picture}
                            alt={`${trainer.first_name} ${trainer.last_name}`}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full bg-gradient-to-br from-orange-300 to-orange-500 flex items-center justify-center text-white font-bold text-xl">
                            {trainer.first_name[0]}{trainer.last_name[0]}
                          </div>
                        )}
                      </div>
                      <h3 className="text-gray-800 mb-2 font-semibold text-lg">
                        {trainer.first_name} {trainer.last_name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                        {trainer.specializations}
                      </p>
                      <div className="flex items-center justify-center space-x-4 text-sm">
                        <div className="flex items-center">
                          <span className="text-yellow-500">⭐</span>
                          <span className="ml-1 text-gray-700">{trainer.rating}</span>
                        </div>
                        <div className="text-gray-700">
                          ${trainer.hourly_rate}/hr
                        </div>
                      </div>
                      <div className="text-xs text-gray-500 mt-2">
                        {trainer.years_experience} years exp • {trainer.total_sessions} sessions
                      </div>

                      {/* Book Now button - appears on hover */}
                      <button className="mt-4 bg-orange-400 text-white px-4 py-2 rounded-full opacity-0 group-hover:opacity-100 transition-opacity text-sm hover:bg-orange-500">
                        View Details & Book
                      </button>
                    </div>
                  ))
                ) : (
                  <div className="col-span-3 text-gray-600">
                    No trainers available at the moment.
                  </div>
                )}
              </div>
            )}

            {/* Trainer Detail Modal */}
            {selectedTrainer && (
              <TrainerDetail
                trainerId={selectedTrainer}
                onClose={handleCloseTrainer}
                onNavigateToProfile={onViewUser}
              />
            )}
          </div>
        );

      case 'services':
        return (
          <div className="text-center">
            <h1 className="text-4xl md:text-5xl mb-6 text-gray-800">
              Our Services
            </h1>
            <p className="text-lg text-gray-700 mb-8 max-w-2xl mx-auto">
              Comprehensive fitness solutions tailored to your individual needs and goals
            </p>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
              {services.map((service, i) => (
                <div
                  key={i}
                  className="bg-white/60 backdrop-blur-sm rounded-lg p-6 border border-white/40 hover:bg-white/80 transition-colors shadow-lg group cursor-pointer"
                  onClick={() => setSelectedService(service)}
                >
                  <h3 className="text-gray-800 mb-2 font-semibold">{service.name}</h3>
                  <p className="text-gray-600 text-sm">{service.desc}</p>
                  <button className="mt-4 text-[#5D4037] text-sm font-medium opacity-0 group-hover:opacity-100 transition-opacity hover:text-[#E6A756] pointer-events-none">
                    Learn More →
                  </button>
                </div>
              ))}
            </div>
          </div>
        );

      case 'admin-panel':
        // Only show admin panel if user is admin
        if (user?.role !== 'admin') {
          return (
            <div className="text-center">
              <h1 className="text-4xl text-gray-800 mb-4">Access Denied</h1>
              <p className="text-gray-600">Admin access required.</p>
            </div>
          );
        }
        return <AdminPanel />;
      default:
        return null;
    }
  };

  return (
    <div className="pt-24 pb-16 px-4 sm:px-6 lg:px-8 min-h-screen flex items-center justify-center">
      <div className="max-w-7xl mx-auto">
        {renderContent()}
      </div>

      {/* Floating Action Button for Create Post - only visible on feed and home */}
      {(activeTab === 'feed' || activeTab === 'home') && (
        <button
          onClick={() => {
            if (activeTab === 'home') {
              navigate('/feed');
            }
            setIsCreatePostModalOpen(true);
          }}
          className="fixed bottom-6 right-6 bg-gradient-to-r from-[#E6A756] to-[#D2824C] text-[#5D4037] w-16 h-16 rounded-full shadow-lg hover:shadow-xl transition-all transform hover:scale-110 flex items-center justify-center z-40 text-2xl font-bold hover:from-[#D2824C] hover:to-[#A0673D]"
          title="Create Post"
        >
          +
        </button>
      )}

      {/* Modals */}
      {selectedPost && (
        <PostModal
          postId={selectedPost}
          onClose={() => setSelectedPost(null)}
        />
      )}

      {/* Create Post Modal */}
      <CreatePostModal
        isOpen={isCreatePostModalOpen}
        onClose={() => setIsCreatePostModalOpen(false)}
        onPostCreated={handlePostCreated}
      />

      {/* Service Details Modal */}
      {selectedService && (
        <ServiceModal
          service={selectedService}
          onClose={() => setSelectedService(null)}
          onFindTrainers={() => {
            setSelectedService(null);
            onTabChange('trainers');
            navigate('/trainers');
          }}
        />
      )}
    </div>
  );
}